<template>
  <v-dialog max-width="640px" v-model="showDialog" persistent>
    <v-card>
      <v-card-title class="secondary white--text py-3 px-5">
        <v-icon color="white" class="mr-2">mdi-file-plus-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Create DA</span>
        <v-spacer />
        <v-btn icon color="white" small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 pt-5 pb-2">
        <v-row dense>
          <v-col cols="6">
            <MultiSelect
              label="Origin *"
              :items="locationList"
              itemText="location_code"
              itemValue="id"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.origin"
            />
          </v-col>
          <v-col cols="6">
            <MultiSelect
              label="Destination *"
              :items="locationList"
              itemText="location_code"
              itemValue="id"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.destination"
            />
          </v-col>
          <v-col cols="6">
            <MultiSelect
              label="Container Type *"
              :items="containerTypeList"
              itemText="type_name"
              itemValue="id"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.container_type"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              label="Job Type *"
              :items="jobTypes"
              outlined
              dense
              hide-details="auto"
              v-model="form.job_type"
            />
          </v-col>
          <v-col cols="6">
            <Autocomplete
              label="Vehicle Registration"
              :items="vehicleList"
              itemText="vehicle_number"
              itemValue="id"
              :loading="vehicleLoading"
              :totalItems="vehicleCount"
              @updateList="fetchVehicles"
              hide-details="auto"
              v-model="form.vehicle"
            />
          </v-col>
          <v-col cols="6">
            <Autocomplete
              label="Driver Name"
              :items="driverList"
              itemText="driver_name"
              itemValue="id"
              :loading="driverLoading"
              :totalItems="driverCount"
              @updateList="fetchDrivers"
              hide-details="auto"
              v-model="form.driver"
            />
          </v-col>
        </v-row>

        <v-divider class="my-3" />

        <div class="d-flex align-center">
          <span class="text-caption grey--text mr-2">Resolved DA Rate</span>
          <v-progress-circular v-if="rateLoading" indeterminate size="16" width="2" color="primary" />
          <span v-else-if="preview.da_rate != null" class="text-subtitle-2 font-weight-bold">
            AED {{ preview.da_rate }}
          </span>
          <span v-else class="text-caption warning--text">
            {{ preview.detail || "Select route, container type and job type" }}
          </span>
        </div>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="px-5 py-3">
        <v-spacer />
        <v-btn depressed outlined @click="closeDialog" class="mr-2">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="submitting"
          :disabled="!isFormValid"
          min-width="120"
          @click="submit"
        >
          <v-icon small class="mr-1">mdi-check-circle</v-icon>
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";

const JOB_TYPES = ["Import", "Export", "FCL", "LCL", "Empty"];

export default {
  name: "CreateContainerDADialog",
  components: { Autocomplete, MultiSelect },
  props: {
    value: { type: Boolean, default: false },
    container: { type: Object, default: () => ({}) },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
        this.fetchInitialData();
        this.fetchRatePreview();
      }
    },
    "form.origin": "fetchRatePreview",
    "form.destination": "fetchRatePreview",
    "form.container_type": "fetchRatePreview",
    "form.job_type": "fetchRatePreview",
  },
  data() {
    return {
      jobTypes: JOB_TYPES,
      form: {
        origin: null,
        destination: null,
        container_type: null,
        job_type: "Import",
        driver: null,
        vehicle: null,
      },
      locationList: [],
      containerTypeList: [],
      vehicleList: [],
      vehicleCount: 0,
      vehicleLoading: false,
      driverList: [],
      driverCount: 0,
      driverLoading: false,
      submitting: false,
      rateLoading: false,
      preview: { da_rate: null, detail: null },
      _rateTimer: null,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isFormValid() {
      return (
        !!this.form.origin &&
        !!this.form.destination &&
        !!this.form.container_type &&
        !!this.form.job_type &&
        this.preview.da_rate != null
      );
    },
  },
  methods: {
    getVal(v) {
      return v && typeof v === "object" ? v.id : v;
    },
    resetForm() {
      this.form = {
        origin: this.container.origin || null,
        destination: this.container.destination || null,
        container_type: this.container.container_type || null,
        job_type: this.container.job_type || "Import",
        // driver/vehicle start null even if the container already has one -
        // Autocomplete (unlike MultiSelect) needs the full {id, name} object
        // to display a pre-filled value, not just the raw id, so these are
        // filled in by fetchInitialData() once that object is fetched.
        driver: null,
        vehicle: null,
      };
      this.preview = { da_rate: null, detail: null };
    },
    closeDialog() {
      this.showDialog = false;
    },
    fetchInitialData() {
      this.$api.location.getLocationSelectList({ limit: "all" }).then((res) => {
        this.locationList = res.data;
      });
      this.$api.containerType.getContainerTypeList({ limit: "all" }).then((res) => {
        this.containerTypeList = res.data;
      });
      this.fetchVehicles({ limit: 10, offset: 0 });
      this.fetchDrivers({ limit: 10, offset: 0 });

      // The container may already carry a resolved driver/vehicle id (e.g.
      // from a prior gate-out) that isn't necessarily within the first page
      // fetched above - resolve it directly by id so Autocomplete has the
      // full object it needs to show a pre-filled selection.
      if (this.container.driver) {
        this.$api.driver.getDriverObject(this.getVal(this.container.driver)).then((res) => {
          this.form.driver = res.data || res;
        });
      }
      if (this.container.vehicle) {
        this.$api.vehicle.getVehicleObject(this.getVal(this.container.vehicle)).then((res) => {
          this.form.vehicle = res.data || res;
        });
      }
    },
    fetchRatePreview() {
      clearTimeout(this._rateTimer);
      const { origin, destination, container_type, job_type } = this.form;
      if (!origin || !destination || !container_type || !job_type) {
        this.preview = { da_rate: null, detail: null };
        return;
      }
      this._rateTimer = setTimeout(() => {
        this.rateLoading = true;
        this.$api.emergencyOrderContainer
          .getDaRatePreview(this.container.id, {
            origin: this.getVal(origin),
            destination: this.getVal(destination),
            container_type: this.getVal(container_type),
            job_type,
          })
          .then((res) => {
            const d = res.data || res;
            this.preview = { da_rate: d.da_rate, detail: d.detail };
          })
          .catch((err) => {
            this.preview = { da_rate: null, detail: err?.message || "Couldn't resolve rate" };
          })
          .finally(() => {
            this.rateLoading = false;
          });
      }, 300);
    },
    fetchVehicles(params = {}) {
      this.vehicleLoading = true;
      this.$api.vehicle
        .getVehicleSelectList({ limit: 10, ...params })
        .then((res) => {
          this.vehicleList = params.offset ? [...this.vehicleList, ...res.data] : res.data;
          this.vehicleCount = res.count;
        })
        .finally(() => {
          this.vehicleLoading = false;
        });
    },
    fetchDrivers(params = {}) {
      this.driverLoading = true;
      this.$api.driver
        .getDriverSelectList({ limit: 10, ...params })
        .then((res) => {
          this.driverList = params.offset ? [...this.driverList, ...res.data] : res.data;
          this.driverCount = res.count;
        })
        .finally(() => {
          this.driverLoading = false;
        });
    },
    submit() {
      this.submitting = true;
      const payload = {
        origin: this.getVal(this.form.origin),
        destination: this.getVal(this.form.destination),
        container_type: this.getVal(this.form.container_type),
        job_type: this.form.job_type,
        driver: this.getVal(this.form.driver),
        vehicle: this.getVal(this.form.vehicle),
      };
      this.$api.emergencyOrderContainer
        .createManualDA(this.container.id, payload)
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "DA created successfully!",
            color: "success",
          });
          this.$emit("daCreated");
          this.closeDialog();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to create DA.",
            color: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
